import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { areasOf, type MockupTemplate, type MockupView as MockupSide, type Placement, type Point } from '@eip/engine';
import { useStore, type DocEntry } from '../state/store';
import { getMockupBlank, getPreviewMaps, getSceneCover, mockupBlankKey, mockupPreviewKey, mockupPreviews, previews, type MockupSlot } from '../state/bitmaps';
import { mockupRunner } from '../app/session';
import { hasWebGL2 } from '../lib/capabilities';
import { glPreviewRender, glPreviewReset, glPreviewUnavailable, onGlPreviewLost } from '../mockup/gl-preview';
import { sceneCoverKey } from '../mockup/gl-job';
import { installEipHook } from '../mockup/parity';
import {
  HANDLE_HIT_PX, ROTATE_HANDLE_PX, areaBoxCorners, bracketTurn, canToggleMockupView, cursorFor, dragToPlacement, dragToRotationAt, dragToScale, hitPrintBox,
  mockupViewShown, nudgePlacement, photoToInches, printBoxCorners, rotateHandleAt, samePlacement,
  type CursorTarget, type PrintBoxCorner, type PrintBoxCorners,
} from '../lib/mockup-view';
import { toImage, type Viewport } from '../lib/viewport';

/** The art a drag scales: the DTF output's pixel size, which fixes the placed
 *  art's aspect ratio exactly as `artRectIn` does in the compositor. */
interface ArtSize { width: number; height: number }

export interface MockupViewState {
  /** True when the mockup is what the canvas is responsible for. `preview` may
   *  still be null inside it — the first render is in flight — and the viewer
   *  shows the DTF preview dimmed until it lands. */
  active: boolean;
  /** The last landed render: the (≤ `MOCKUP_PREVIEW_MAX`) bitmap with the
   *  FULL-RES photo size it is drawn at, so the canvas works in photo pixels. */
  preview: { bitmap: ImageBitmap; width: number; height: number } | null;
  /** The blank being rendered into, for the HUD's name and the print-area
   *  outline. Its `preview.width`/`height` ARE the prepared photo's size, which
   *  is why the viewport needs no separate reading of it. */
  template: MockupTemplate | null;
  rendering: boolean;
  /** A render landed, but the DTF output has moved since it was composited. */
  stale: boolean;
  clipped: boolean;
  /** Which of the pair is on canvas. `'back'` only ever survives while
   *  `canToggleView` holds, so nothing has to write to the store to repair it. */
  view: MockupSide;
  /** A back design is bound, the pair's `backJob` has been derived and the
   *  style's BACK blank is prepared: without all three there is nothing to
   *  switch to, and the viewer hides the toggle entirely. */
  canToggleView: boolean;
  setView(next: MockupSide): void;
  /** The transform box's four corners in PHOTO pixels, clockwise from top-left,
   *  for the placement currently on canvas — a drag's live one while a gesture
   *  is in hand, the job's own otherwise. Null before the first render, or for a
   *  print area too degenerate to map through. */
  corners: PrintBoxCorners | null;
  /** The angle the box on canvas is drawn at — a drag's live one while a gesture is in hand, the
   *  job's own otherwise. The HUD reports it, and the viewer's rotate handle hangs off it. */
  rotationDeg: number;
  /** One box per ENABLED extra print, in the order they composite, in photo pixels. Outlines
   *  only in this task: extra prints are placed by the panel's numbers, not by the pointer, and
   *  nothing here hit-tests them. Empty for a blank with one area, which is every shipped one. */
  extraBoxes: PrintBoxCorners[];
  /** The areas the last render could not print (this slot's own: `skipped`, or `backSkipped` on
   *  the back view), for the HUD. */
  skipped: string[];
  /** The art-free garment for this slot, once one is cached: what a ghost drag
   *  paints under the art. Null until the first drag has fetched it. */
  blank: { bitmap: ImageBitmap; width: number; height: number } | null;
  /** The DTF output preview the ghost draws as the art — a placement aid, not a
   *  render: no ink look, no shading, no wrinkle. */
  ghostArt: ImageBitmap | null;
  /** True while the canvas should paint `blank` + `ghostArt` + the box instead
   *  of the landed render: during a ghost drag, and until the render the commit
   *  asked for lands. */
  ghosting: boolean;
  /** Bumped once per animation frame while a ghost drag runs (and whenever the
   *  ghost is dropped), so the viewer's draw effect repaints with NO store
   *  write, no worker render and no re-render of the rest of the app. */
  frame: number;
  /**
   * The GPU's own frame for exactly what is on canvas right now — the current job, with a drag's
   * live placement folded in — drawn scaled to the photo's size in place of BOTH the CPU render and
   * the blank+ghost pair. Null whenever there is no GPU frame for the current state (no WebGL2, the
   * context is lost, the template's maps are not cached yet, no art to print), and the viewer falls
   * back to exactly what it drew before this existed.
   *
   * The canvas OBJECT is the same one every frame (it is the singleton renderer's), so it cannot be
   * a repaint trigger on its own — `glVersion` is.
   */
  glFrame: HTMLCanvasElement | null;
  /** Bumped once per GPU frame actually rendered. What tells the viewer's draw effect that the
   *  pixels inside `glFrame` have changed — for a drag `frame` moves with it, but a panel edit
   *  (a slider, the colour, the rotation field) changes the job with no frame bump and no landed
   *  CPU render for ~100 ms, and without this the canvas would keep showing the previous GPU frame. */
  glVersion: number;
  /** Which renderer drew what is on canvas THIS frame. The store's `glPreview` is the sticky,
   *  app-wide version the inspector row and the banner read. */
  renderer: 'gl' | 'cpu';
  /** Pointer handlers for the canvas, each returning true when the mockup has
   *  taken the event — the viewer pans with whatever they leave. `vp` is passed
   *  in rather than read from a ref so a drag always uses the transform the
   *  frame it belongs to was drawn with. */
  onPointerDown(e: ReactPointerEvent<HTMLCanvasElement>, vp: Viewport | null): boolean;
  onPointerMove(e: ReactPointerEvent<HTMLCanvasElement>, vp: Viewport | null): boolean;
  onPointerUp(e: ReactPointerEvent<HTMLCanvasElement>, vp: Viewport | null): boolean;
  /** Abandons a drag: the gesture is over without an ending, so nothing is
   *  written and the ghost is dropped. No viewport is needed — there is no
   *  point to convert. */
  onPointerCancel(e: ReactPointerEvent<HTMLCanvasElement>): boolean;
  /** The canvas's keyboard placement: arrows move the print, `[`/`]` turn it.
   *  True when the key was ours, so the viewer knows the page must not also
   *  scroll. No viewport is involved — a key press is in inches, not pixels. */
  onKeyDown(e: ReactKeyboardEvent<HTMLCanvasElement>): boolean;
}

/** How far one arrow press moves the print, and how far one with Shift held:
 *  a tenth of an inch is finer than any hand on a trackpad, an inch is a
 *  deliberate reposition. `[`/`]` turn by a degree, or 15 with Shift — the same
 *  two steps the rotation DRAG snaps to. */
const NUDGE_IN = 0.1, NUDGE_SHIFT_IN = 1, NUDGE_DEG = 1, NUDGE_SHIFT_DEG = 15;

/** What one gesture writes: the two job fields the transform box owns. */
interface JobEdit { placement: Placement; rotationDeg: number }

/** A placement drag in progress. The template, the art size and the placement
 *  are all snapshotted at pointer-down: every move recomputes from them (rather
 *  than from the last frame), so a throttled drag ends exactly where the pointer
 *  is and a panel edit landing mid-drag cannot bend the gesture. */
interface Drag {
  pointerId: number;
  docId: string;
  from: Point;
  start: Placement;
  /** The angle the gesture started at, so a rotate drag measures from it and a corner scale
   *  un-turns the pointer by it — both from the snapshot, never from the frame before. */
  startRotation: number;
  template: MockupTemplate;
  art: ArtSize;
  /** What the press grabbed: the rotate handle turns the art, a corner handle scales about the
   *  opposite corner, anything else inside the print area moves it. */
  mode: PrintBoxCorner | 'move' | 'rotate';
  /** True when a blank was cached when the press landed: this drag paints a
   *  ghost and writes ONCE on release. False is the live-render drag this
   *  viewer always had — the fallback, so a first drag never regresses. */
  ghost: boolean;
}

/** The placement the canvas is showing instead of the job's own. */
interface Ghost {
  /** Which cache slot (`mockupPreviewKey`) it belongs to, so a view toggle or a
   *  document switch can never leave one ghosting over another canvas. */
  slotKey: string;
  placement: Placement;
  rotationDeg: number;
  /** False while the pointer is down. Once released the ghost is HELD — still
   *  drawn — until the render the commit asked for lands, so the art does not
   *  flash back to where it was for the ~100 ms in between. */
  held: boolean;
  /** The slot's `renderedGeneration` at the commit: the ghost is dropped as soon
   *  as that moves, which is exactly when the real render is on the canvas. */
  at: number | undefined;
}

/**
 * Everything the viewer needs in mockup mode: which document's render is on
 * canvas (the front design, or the back one when the pair is bound), the cached
 * bitmap for it, the transform box that shows where the art sits, and the drag
 * that places and scales it by hand.
 *
 * It lives beside `Viewer` for the same reason `useSepView` does — it owns refs
 * and effects of its own — and it is what keeps a drag off the store: a gesture
 * moves a ref and repaints, and only the pointer-up writes.
 *
 * Nothing here runs outside mockup mode: `active` is false, every handler
 * returns false on its first line, and no effect does any work, so the DTF and
 * screen-print paths are untouched.
 */
export function useMockupView(doc: DocEntry | undefined, canvasRef: RefObject<HTMLCanvasElement | null>, vp: Viewport | null): MockupViewState {
  const appMode = useStore((s) => s.appMode);
  const templates = useStore((s) => s.templates);
  const templateStatus = useStore((s) => s.templateStatus);
  const viewPref = useStore((s) => s.viewer.mockupView);
  const setViewer = useStore((s) => s.setViewer);

  const active = mockupViewShown(appMode, doc);
  // Both views belong to THIS document's mockup: the front is its own art on
  // its own blank, the back is the bound document's art on the style's back
  // blank (`backJob`, derived by the session and rendered into a second slot).
  // `view` chooses which of the two this hook reports — including which bitmap,
  // which blank and which half of the bookkeeping.
  const mockup = active ? doc?.mockup : undefined;
  const backId = active ? mockup?.backDocId ?? null : null;
  // Only the back document, never the whole `docs` array: subscribing to the
  // array would re-render the viewer on every unrelated per-document write —
  // a run's progress on another file, an inspection landing — in DTF and
  // screen-print mode as much as in mockup mode. `find` returns the same entry
  // object until that document itself changes, so the default `Object.is`
  // comparison is exactly the right one.
  const backDoc = useStore((s) => (backId ? s.docs.find((d) => d.id === backId) : undefined));
  const canToggleView = canToggleMockupView(mockup, backDoc, templates, templateStatus);
  // Derived, never written back: a back design unbound (or its blank unloaded)
  // while the back view was showing falls to the front here, exactly as a
  // removed plate falls back to the proof in `useSepView`.
  const view: MockupSide = canToggleView && viewPref === 'back' ? 'back' : 'front';
  const onBack = view === 'back';
  const slot: MockupSlot = onBack ? 'back' : 'front';
  // The document whose ART is on screen — and whose job a placement drag edits.
  // For the back view that is the BOUND document: `backJob` is derived from its
  // own job, so a drag writes to the source the derivation reads.
  const shown = onBack ? backDoc : doc;
  const shownId = shown?.id;
  const job = onBack ? mockup?.backJob : mockup?.job;
  const template = (job && templates.find((t) => t.template.id === job.templateId)?.template) ?? null;
  const renderedGeneration = onBack ? mockup?.backRenderedGeneration : mockup?.renderedGeneration;
  const slotError = onBack ? mockup?.backError : mockup?.error;
  // Where the render for this view was cached — and, with it, the blank a drag
  // ghosts on. A pair holds two bitmaps at once so the toggle is instant rather
  // than a second of worker time each way.
  const previewKey = doc && onBack ? mockupPreviewKey(doc.id, 'back') : shownId;

  /** Bumped to repaint the canvas without touching the store: once per animation
   *  frame of a ghost drag, and once when a ghost is dropped or a blank lands. */
  const [frame, setFrame] = useState(0);
  const bump = useCallback(() => setFrame((f) => f + 1), []);

  // `mockupPreviews` is a plain Map, not reactive. `renderedGeneration` is what
  // makes this lookup run again: the runner caches the bitmap BEFORE the store
  // write that moves it, so the generation and the bitmap always arrive
  // together. Matching them is also what keeps a bitmap from an older render
  // (or from a document that has since been dropped, which evicts the entry
  // entirely) off the canvas.
  const preview = useMemo(() => {
    if (!active || !previewKey || renderedGeneration === undefined) return null;
    const entry = mockupPreviews.get(previewKey);
    if (!entry || entry.generation !== renderedGeneration) return null;
    return { bitmap: entry.bitmap, width: entry.width, height: entry.height };
  }, [active, previewKey, renderedGeneration]);

  const run = shown?.run;
  const runGeneration = onBack ? mockup?.backRunGeneration : mockup?.runGeneration;
  const stale = renderedGeneration !== undefined && !!run && runGeneration !== run.generation;
  const art = useMemo<ArtSize | null>(() => (run ? { width: run.width, height: run.height } : null), [run?.width, run?.height]);

  // ---- the placement drag ---------------------------------------------------
  const dragRef = useRef<Drag | null>(null);
  const ghostRef = useRef<Ghost | null>(null);
  /** The placement the next frame will write, and the frame that will write it,
   *  for the LIVE-RENDER fallback only (no blank cached yet). A pointer-move can
   *  arrive several times per frame (and a coalesced one more often still);
   *  every one of them would otherwise be a store write, a re-render of the
   *  whole app and a re-armed 60 ms render debounce. */
  const pendingRef = useRef<{ docId: string; edit: JobEdit } | null>(null);
  const writeFrameRef = useRef<number | null>(null);
  /** The animation frame a ghost drag repaints on — a redraw, never a write. */
  const drawFrameRef = useRef<number | null>(null);

  /** Write a placement and an angle, once. Returns false when there was nothing
   *  to write, so a commit that changed nothing does not leave a ghost waiting
   *  for a render that will never come. */
  const commit = useCallback((docId: string, edit: JobEdit): boolean => {
    // Read the job back at WRITE time rather than capturing it at pointer-down:
    // a colour change (which rewrites `templateId`/`garment`) landing mid-drag
    // would otherwise be undone by the write that ends the gesture.
    const st = useStore.getState();
    const current = st.docs.find((d) => d.id === docId)?.mockup?.job;
    if (!current) return false;
    if (samePlacement(current.placement, edit.placement) && (current.rotationDeg ?? 0) === edit.rotationDeg) return false;
    st.setMockupJob(docId, { ...current, placement: edit.placement, rotationDeg: edit.rotationDeg });
    return true;
  }, []);

  const flush = useCallback(() => {
    if (writeFrameRef.current !== null) { cancelAnimationFrame(writeFrameRef.current); writeFrameRef.current = null; }
    const p = pendingRef.current;
    pendingRef.current = null;
    if (p) commit(p.docId, p.edit);
  }, [commit]);

  const queue = useCallback((docId: string, edit: JobEdit) => {
    pendingRef.current = { docId, edit };
    if (writeFrameRef.current !== null) return;
    writeFrameRef.current = requestAnimationFrame(() => { writeFrameRef.current = null; flush(); });
  }, [flush]);

  /** One repaint per animation frame, however many pointer moves arrived. */
  const scheduleDraw = useCallback(() => {
    if (drawFrameRef.current !== null) return;
    drawFrameRef.current = requestAnimationFrame(() => { drawFrameRef.current = null; bump(); });
  }, [bump]);

  /** Drop the gesture and everything it had queued or drawn, writing nothing. */
  const abandon = useCallback(() => {
    dragRef.current = null;
    ghostRef.current = null;
    pendingRef.current = null;
    if (writeFrameRef.current !== null) { cancelAnimationFrame(writeFrameRef.current); writeFrameRef.current = null; }
    if (drawFrameRef.current !== null) { cancelAnimationFrame(drawFrameRef.current); drawFrameRef.current = null; }
    // The grabbing cursor belonged to the gesture that has just gone away; the
    // next move over the canvas picks the right one up again.
    const c = canvasRef.current;
    if (c) c.style.cursor = '';
    bump();
  }, [bump, canvasRef]);

  // Leaving mockup mode (or losing the document, or switching which slot is on
  // canvas) mid-drag: drop the gesture rather than letting a stray frame write
  // to a job the user is no longer looking at, or ghost over another canvas.
  useEffect(() => {
    if (!active && (dragRef.current || ghostRef.current)) abandon();
  }, [active, abandon]);
  // Keyed on the SLOT alone — never on the placement, which is the one thing a
  // ghost is expected to change out from under the job.
  useEffect(() => {
    if (dragRef.current || ghostRef.current) abandon();
  }, [previewKey, abandon]);

  useEffect(() => () => {
    if (writeFrameRef.current !== null) cancelAnimationFrame(writeFrameRef.current);
    if (drawFrameRef.current !== null) cancelAnimationFrame(drawFrameRef.current);
  }, []);

  // A held ghost lasts exactly until the render it is standing in for lands —
  // or until that render fails, which is the other way the wait ends.
  useEffect(() => {
    const g = ghostRef.current;
    if (!g || !g.held) return;
    if (renderedGeneration === g.at && !slotError) return;
    ghostRef.current = null;
    bump();
  }, [renderedGeneration, slotError, bump]);

  // Escape abandons a drag in flight: the same discard a pointer-cancel is, for
  // a gesture the user has thought better of with the button still down.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && dragRef.current) abandon(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, abandon]);

  /** A client point in PHOTO pixels. In mockup mode the viewport's image is the
   *  photo itself (the preview bitmap is drawn scaled up to it), so the viewport
   *  transform is the whole conversion. */
  const pointAt = useCallback((e: { clientX: number; clientY: number }, v: Viewport | null): Point | null => {
    const c = canvasRef.current;
    if (!c || !v || !(v.scale > 0)) return null;
    const r = c.getBoundingClientRect();
    const p = toImage(v, e.clientX - r.left, e.clientY - r.top);
    return [p.x, p.y];
  }, [canvasRef]);

  // ---- what the canvas draws ------------------------------------------------
  const ghost = ghostRef.current && ghostRef.current.slotKey === previewKey ? ghostRef.current : null;
  const placement = ghost?.placement ?? job?.placement ?? null;
  const rotationDeg = ghost ? ghost.rotationDeg : job?.rotationDeg ?? 0;
  // A stable identity for an unmoved box, so the viewer's draw effect is not
  // re-run by every unrelated store write.
  const boxKey = placement ? `${placement.widthIn}:${placement.xIn}:${placement.yIn}:${rotationDeg}` : '';
  // `placement` is depended on through `boxKey`: the four numbers that move the
  // box, rather than an object that is new on every frame of a drag.
  const corners = useMemo(
    () => (active && preview && template && placement && art ? printBoxCorners(template, placement, art, rotationDeg) : null),
    [active, preview, template, boxKey, art],
  );
  // The extra prints' boxes. Keyed like the primary one: the fields that move a box, as a string,
  // so an unrelated store write cannot churn the viewer's draw effect.
  const extraPrints = job?.extraPrints ?? [];
  const extraKey = extraPrints.map((p) => `${p.areaId}|${p.docId ?? ''}|${p.enabled}|${p.placement.widthIn}|${p.placement.xIn}|${p.placement.yIn}|${p.rotationDeg}`).join(';');
  // The art SIZES those boxes need, as a string — never the documents themselves. Subscribing to
  // `s.docs` here would re-render the viewer on every unrelated per-document write (a run's
  // progress on another file, an inspection landing) in every mode; this value only changes when
  // a size a box is drawn from does. A print on this document's own art (`docId: null`, or its
  // own id — the two are the same print to `extraArtsFor`) measures the art on canvas.
  const extraArtKey = useStore((st) => extraPrints.map((p) => {
    const d = p.docId && p.docId !== shownId ? st.docs.find((x) => x.id === p.docId) : shown;
    return d?.run ? `${d.run.width}x${d.run.height}` : '';
  }).join(';'));
  const extraBoxes = useMemo(() => {
    if (!active || !preview || !template) return [];
    const areas = areasOf(template);
    const sizes = extraArtKey.split(';');
    const out: PrintBoxCorners[] = [];
    extraPrints.forEach((p, i) => {
      // A disabled print is not composited, so there is nothing on the garment to outline; an
      // area this blank does not have, or a document with no art, comes back from the render as
      // `skipped` and the HUD says so instead.
      if (!p.enabled) return;
      const area = areas.find((a, j) => j > 0 && a.id === p.areaId);
      const [w, h] = (sizes[i] ?? '').split('x').map(Number);
      if (!area || !(w > 0) || !(h > 0)) return;
      const c = areaBoxCorners(area, p.placement, { width: w, height: h }, p.rotationDeg);
      if (c) out.push(c);
    });
    return out;
    // `extraPrints` is depended on through `extraKey`, the same discipline as `boxKey` above.
  }, [active, preview, template, extraKey, extraArtKey]);
  // The SAME function the runner caches under, from the same registry lookup
  // (`template` is `job.templateId`'s entry), so the two can never disagree about
  // the key. Computed inside the selector — never by subscribing to `s.docs` —
  // because the key is a string: the run generations it carries re-render the
  // viewer only when one of them actually moves, exactly as `extraArtKey` above.
  const blankKey = useStore((st) => (job ? mockupBlankKey(job, template, st.docs, shownId) : null));
  // `mockupBlanks` is a plain Map, not reactive: `frame` — bumped when a fetch
  // resolves — is what makes this look again.
  const blank = useMemo(
    () => (active && previewKey && blankKey ? getMockupBlank(previewKey, blankKey) ?? null : null),
    [active, previewKey, blankKey, frame],
  );
  const ghostArt = active && shownId ? previews.get(shownId)?.output?.bitmap ?? null : null;
  const ghosting = !!ghost && !!blank && !!ghostArt;

  // ---- the GPU frame --------------------------------------------------------
  const setGlPreview = useStore((s) => s.setGlPreview);
  const templateId = job?.templateId;
  // Which PREPARATION of that template the worker is holding. Half the identity of a set of cached
  // preview maps (see `previewMapsStale`), and what the fetch below is armed on: re-preparing a
  // template — a user blank re-saved from the editor — changes every map it carries.
  const prepGeneration = templateId ? templateStatus[templateId]?.prepared?.prepGeneration : undefined;
  // `previewMaps` is a plain Map, not reactive, exactly like `mockupBlanks` above: `frame` — bumped
  // when the fetch resolves — is what makes this look again. Maps built from an older preparation
  // are refused rather than drawn from, so an editor re-save cannot leave the GPU painting the
  // previous blank's wrinkles under the art.
  const maps = useMemo(() => {
    if (!active || !templateId || prepGeneration === undefined) return null;
    const m = getPreviewMaps(templateId);
    return m && m.prepGeneration === prepGeneration ? m : null;
  }, [active, templateId, prepGeneration, frame]);

  /** The backdrop scene this job names, or null for a colour, a gradient or the photo's own — and
   *  the key its cover is cached (and uploaded) under, which carries the preview size because the
   *  pass fetches the cover pixel for pixel.
   *
   *  `maps.shadow` and not just `maps`: that is the same predicate `glJobFor` uses to force kind 0,
   *  so on a blank whose mask does not separate there is nothing to cut a cover FOR — and cutting
   *  one anyway cost a Lanczos resample in the worker, a transfer, a ~9 MB texture upload and a
   *  needless spell of CPU frames while `glFrame` waited for it. */
  const backdropSceneId = job?.backdrop?.kind === 'scene' ? job.backdrop.sceneId : null;
  const coverKey = backdropSceneId && maps?.shadow ? sceneCoverKey(backdropSceneId, maps.width, maps.height) : null;
  // `sceneCovers` is a plain Map, not reactive, exactly like `previewMaps` above: `frame` — bumped
  // when the cut below resolves — is what makes this look again.
  const cover = useMemo(() => (coverKey ? getSceneCover(coverKey) ?? null : null), [coverKey, frame]);
  // Whether the WORKER holds the scene: the cut is made from its decoded pixels, so asking before
  // the decode lands is a call that throws. A boolean selected out of the store rather than the
  // status object, so the effect below re-runs exactly when the answer changes — which is the one
  // thing that turns its no-op into a cut.
  const sceneReady = useStore((s) => !!backdropSceneId && s.sceneStatus[backdropSceneId]?.state === 'ready');

  // Each art the GPU frame prints, as a version string. The bitmaps live in `previews`, which is a
  // plain Map whose entries are REPLACED (and closed) when a run lands a new output, so the memo
  // below needs something in its deps that moves when one does: `previewVersion` bumps in the same
  // pass, just after the new bitmap is cached. A string computed inside the selector — never a
  // subscription to `s.docs` — for the same reason `extraArtKey` above is one.
  const artVersionKey = useStore((st) => [shownId ?? '', ...extraPrints.filter((p) => p.enabled).map((p) => p.docId ?? shownId ?? '')]
    .map((id) => `${id}@${st.docs.find((d) => d.id === id)?.previewVersion ?? -1}`).join(';'));

  /** Bumped by the memo below once per frame the GPU actually drew — the viewer's repaint trigger
   *  for a job change that comes with no animation frame of its own (a panel edit). */
  const glVersionRef = useRef(0);
  /**
   * DELIBERATELY IMPURE, and the alternative is worse. The factory below runs a synchronous GPU
   * render and bumps a ref, both during the render phase — which is the one place React asks you not
   * to do either. It is here rather than in a `useEffect` because an effect runs AFTER the commit:
   * the viewer's draw effect would paint the canvas from the PREVIOUS GPU frame, and the new one
   * would only reach the screen on the following animation frame. A drag at 60 Hz cannot afford a
   * frame of latency on every move, and half the point of this task is that the GPU is a gesture
   * AHEAD of the CPU, not a frame behind it.
   *
   * What that costs, both harmless: React double-invokes render in dev (`<StrictMode>` in
   * `main.tsx`), so every update renders the GPU frame twice and `glVersion` advances by 2 — two
   * identical frames into the same buffer, one extra repaint; and a discarded concurrent render has
   * the same shape, since the render that commits is the last one to have touched the shared canvas.
   * Neither can produce a STALE frame, which is the only failure that would matter.
   */
  const glFrame = useMemo(() => {
    // Nothing to draw with: no maps cached (or none that match this preparation), no template, no
    // job, or no art for the primary print — which is the one print a mockup cannot be without.
    if (!maps || !template || !job || !shownId || !ghostArt || !placement) return null;
    // A SCENE backdrop whose cover has not been cut yet: the base pass would fall back to the
    // photo's own backdrop (`glJobFor` forces kind 0 without the cover), which is the one thing the
    // shop just asked to change. The CPU composite underneath is a slower preview of the RIGHT
    // picture, so the GPU sits this frame out and the effect below asks for the cover.
    if (coverKey && !cover) return null;
    const arts: Record<string, ImageBitmap> = { [shownId]: ghostArt };
    for (const p of extraPrints) {
      if (!p.enabled) continue;
      const id = p.docId ?? shownId;
      // A print whose document has no output is simply left out of the GPU frame, as `glJobFor`
      // leaves it out of the job: the CPU render is what reports it as skipped, and the HUD says so.
      const bitmap = previews.get(id)?.output?.bitmap;
      if (bitmap) arts[id] = bitmap;
    }
    // The LIVE job. During a ghost drag that is the job with the gesture's own placement and angle
    // folded in — the same two numbers the ghost paints — so the GPU shows the full simulation
    // (shading, wrinkle, keystone, ink look) where the ghost showed flat art. Off a drag,
    // `placement` and `rotationDeg` ARE the job's own, which is how a panel edit gets here too.
    const canvas = glPreviewRender(maps, template, { ...job, placement, rotationDeg }, arts, shownId, coverKey && cover ? { [coverKey]: cover } : {});
    if (canvas) glVersionRef.current += 1;
    return canvas;
    // `placement`/`rotationDeg` are depended on through `boxKey` — the four numbers that move the
    // box — rather than through objects that are new on every frame of a drag, the same discipline
    // `corners` uses. `frame` is what re-runs this during a gesture, and is also what bounds the
    // whole thing to ONE GPU frame per animation frame: nothing else in these deps moves inside one.
  }, [maps, template, job, shownId, ghostArt, boxKey, extraKey, artVersionKey, coverKey, cover, frame]);
  const glVersion = glVersionRef.current;

  // The store's copy of which renderer is on, for the inspector's Renderer row and the App banner.
  // Written from an effect and not from the memo above, because that memo runs during the render
  // pass and a store write there is a write into another component's render.
  // Gated on `active` so `hasWebGL2` — which allocates a real context to answer — is not probed at
  // all outside mockup mode, where nothing reads the answer.
  useEffect(() => {
    if (!active) return;
    if (!hasWebGL2()) { setGlPreview('cpu'); return; }
    if (glFrame) setGlPreview('gl');
  }, [active, glFrame, setGlPreview]);

  // A lost context: every texture, program and framebuffer the renderer held died with it, so it is
  // thrown away and a new one is built lazily by the next frame that wants one. Nothing else has to
  // happen here — `glFrame` goes null, and the viewer is back on the CPU render (and on the
  // blank+ghost during a drag) by itself.
  useEffect(() => onGlPreviewLost(() => {
    glPreviewReset();
    // Both deferred, because a loss can be reported from inside the GPU render above — which runs
    // during React's render pass, where neither a store write nor a `setState` belongs.
    //
    // The `bump` is not decoration. A loss raised by the browser ITSELF (a GPU reset, the tab
    // backgrounded long enough to be dropped) arrives from an event listener rather than from a
    // render, and the canvas's drawing buffer goes with the context — so without a repaint the
    // viewer would sit on a blank mockup until the next unrelated store write. This is what asks
    // the hook for one more frame, which either draws through a rebuilt context or falls back.
    queueMicrotask(() => { setGlPreview('lost'); bump(); });
  }), [setGlPreview, bump]);

  // The Playwright seam (`window.__eip`), published once — from here because this hook is what
  // mounts with the viewer and owns the GPU path the seam reports on.
  useEffect(() => { installEipHook(); }, []);

  /** The blank for the slot on canvas, fetched at most once per (template,
   *  colour). Called as a drag starts, and again as it ends so the next gesture
   *  has one in hand.
   *
   *  The repaint is asked for only when there was actually something to fetch:
   *  `mockupBlanks` is a plain Map, so a blank that LANDS needs a `bump` for the
   *  memo to see it — but for one already cached `ensureBlank` resolves in the
   *  same microtask, and the bump would be a second identical GPU frame on top
   *  of the one the commit at pointer-up already renders. */
  const warmBlank = useCallback(() => {
    if (!doc) return;
    const cached = !!previewKey && !!blankKey && !!getMockupBlank(previewKey, blankKey);
    const fetching = mockupRunner.ensureBlank(doc.id, slot);
    if (cached) void fetching.catch(() => {});
    else void fetching.then(bump, () => {});
  }, [doc?.id, slot, bump, previewKey, blankKey]);

  // Fetched for the slot ON CANVAS after every landed render — which is both
  // when a blank first becomes worth having (the mockup is up, a drag is the
  // obvious next move) and when a template or colourway change has invalidated
  // the last one. `renderedGeneration` is the key rather than a timer: it moves
  // exactly once per render, and `ensureBlank` is a no-op for a blank already
  // cached, so the steady state costs one map lookup. It is deliberately NOT
  // done from the session: that would keep a blank alive for every open
  // document rather than for the one being looked at.
  useEffect(() => {
    if (!active || !previewKey || !blankKey || renderedGeneration === undefined) return;
    if (getMockupBlank(previewKey, blankKey)) return;
    warmBlank();
  }, [active, previewKey, blankKey, renderedGeneration, warmBlank]);

  // The template's preview maps, fetched as soon as the mockup is up rather than when a drag starts:
  // they are what the GPU renders from, they cost one worker call per PREPARATION (not per job,
  // colourway or placement), and fetching them up front is what makes the first drag a GPU drag from
  // its first frame instead of a ghost. Keyed on the preparation for the same reason the cache is:
  // that is the one thing that invalidates them. `ensurePreviewMaps` is a no-op for maps already
  // cached and never rejects, so the steady state costs one map lookup and a failure costs nothing
  // but the CPU path we already have.
  useEffect(() => {
    if (!active || !templateId || prepGeneration === undefined) return;
    // Nothing could ever draw them: a browser with no WebGL2 at all, a renderer it refused to build,
    // or a GPU that kept dropping its context. The maps are a worker call and tens of MB of typed
    // arrays plus a bitmap PER TEMPLATE, retained for the life of the page — so fetching them where
    // there is no GPU path would spend both on exactly the machines least able to afford it. Checked
    // inside the effect rather than in its deps because it is not reactive: it only ever goes from
    // false to true, and the cost of finding out one fetch late is one fetch.
    if (glPreviewUnavailable()) return;
    void mockupRunner.ensurePreviewMaps(templateId).then(bump, () => {});
  }, [active, templateId, prepGeneration, bump]);

  // The backdrop scene's cover, cut to the preview maps' exact size — the one texture the GPU needs
  // that the maps do not carry, because it belongs to the JOB rather than to the blank. Asked for
  // only once there are maps (the size is theirs) and only where a GPU frame could use it, on the
  // same terms as the maps themselves. `ensureSceneCover` is a no-op for a cover already cached and
  // never rejects, so the steady state costs one map lookup; until it lands `glFrame` is null and
  // the viewer shows the CPU render, which is already drawing the same backdrop.
  useEffect(() => {
    if (!active || !backdropSceneId || !sceneReady || !maps || !coverKey || cover) return;
    if (glPreviewUnavailable()) return;
    void mockupRunner.ensureSceneCover(backdropSceneId, maps.width, maps.height).then(bump, () => {});
  }, [active, backdropSceneId, sceneReady, maps, coverKey, cover, bump]);

  useEffect(() => {
    const c = canvasRef.current;
    if (c && !active) c.style.cursor = '';
  }, [active, canvasRef]);

  /** The scale a screen-pixel measurement is divided by to reach photo pixels. */
  const scaleOf = (v: Viewport | null): number => {
    const scale = (v ?? vp)?.scale;
    return scale && scale > 0 ? scale : 1;
  };

  /** What the pointer is over right now, for the cursor and for a press. */
  const targetAt = (px: Point | null, v: Viewport | null): CursorTarget => {
    if (!px || !template || !corners) return px && template && photoToInches(template, px) ? 'area' : null;
    const scale = scaleOf(v);
    const hit = hitPrintBox(px, corners, HANDLE_HIT_PX / scale, rotateHandleAt(corners, ROTATE_HANDLE_PX / scale));
    if (hit) return hit;
    // Outside the art but inside the print area: a press there moves the art
    // too, which is what this viewer has always done.
    return photoToInches(template, px) ? 'area' : null;
  };

  const setCursor = (cursor: string) => {
    const c = canvasRef.current;
    if (c) c.style.cursor = cursor;
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>, v: Viewport | null): boolean => {
    // A drag only makes sense against a render that is actually on the canvas:
    // before the first one lands there is no box to aim at and the DTF stand-in
    // is in a different coordinate space.
    if (!active || !preview || !template || !shownId || !job || !art || !previewKey) return false;
    // Primary button only: a right-click (context menu) or a middle-click must
    // not start placing art, and neither delivers the pointerup that would end it.
    if (e.button !== 0) return false;
    const px = pointAt(e, v ?? vp);
    const target = targetAt(px, v);
    // Outside the print area (or with no viewport yet) this is not a placement
    // gesture at all, and the viewer's own pan takes it.
    if (!px || target === null) return false;
    const mode: PrintBoxCorner | 'move' | 'rotate' = target === 'move' || target === 'area' || target === 'dragging' ? 'move' : target;
    // Whether this gesture ghosts is settled here and not revisited: a blank
    // landing mid-drag must not switch a gesture from one kind to the other
    // half way through. The fetch is for the NEXT drag — the first one on a
    // fresh blank runs the live-render path this viewer always had.
    warmBlank();
    const ghosted = !!blank && !!ghostArt;
    dragRef.current = { pointerId: e.pointerId, docId: shownId, from: px, start: job.placement, startRotation: job.rotationDeg ?? 0, template, art, mode, ghost: ghosted };
    if (ghosted) {
      ghostRef.current = { slotKey: previewKey, placement: job.placement, rotationDeg: job.rotationDeg ?? 0, held: false, at: renderedGeneration };
      bump();
    }
    // Both the move and the turn are "the pointer is holding this": `grabbing` for as long as the
    // gesture lasts. A corner keeps its own diagonal arrow.
    setCursor(cursorFor(mode === 'move' || mode === 'rotate' ? 'dragging' : mode));
    return true;
  };

  /** Where a drag has got to: the move gesture, the corner scale, or the turn. */
  const editFor = (d: Drag, px: Point, shift: boolean): JobEdit => {
    if (d.mode === 'rotate') {
      return { placement: d.start, rotationDeg: dragToRotationAt(d.startRotation, d.start, d.from, px, d.template, d.art, shift) };
    }
    const next = d.mode === 'move'
      ? dragToPlacement(d.start, d.from, px, d.template, d.art, shift)
      // A turned box is scaled about its turned corner: `dragToScale` un-turns the pointer by the
      // angle the gesture started at and pins that corner again.
      : dragToScale(d.start, d.mode, d.from, px, d.template, d.art, shift, d.startRotation);
    return { placement: next, rotationDeg: d.startRotation };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>, v: Viewport | null): boolean => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) {
      // No gesture of ours: the cursor is the only thing a move changes, and
      // setting it on the element costs no render.
      if (active && preview) setCursor(cursorFor(targetAt(pointAt(e, v ?? vp), v)));
      return false;
    }
    const px = pointAt(e, v ?? vp);
    // Still ours even when the point is unusable: releasing the drag here would
    // hand the rest of the gesture to the pan and drag the art AND the canvas.
    if (px) {
      const next = editFor(d, px, e.shiftKey);
      if (d.ghost) {
        // The whole point of the ghost: a ref and a repaint, no store write, no
        // worker render, no bitmap transfer.
        if (ghostRef.current) { ghostRef.current = { ...ghostRef.current, ...next }; scheduleDraw(); }
      } else {
        queue(d.docId, next);
      }
    }
    return true;
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>, v: Viewport | null): boolean => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return false;
    dragRef.current = null;
    const px = pointAt(e, v ?? vp);
    setCursor(cursorFor(targetAt(px, v)));
    // The final placement is written now rather than on the next frame: the
    // gesture is over, and a frame that never comes (the tab hidden the instant
    // the pointer lifts) would leave the art where the last throttled write put it.
    const g = ghostRef.current;
    const next: JobEdit | null = px ? editFor(d, px, e.shiftKey) : g ? { placement: g.placement, rotationDeg: g.rotationDeg } : null;
    if (!d.ghost) {
      if (next) pendingRef.current = { docId: d.docId, edit: next };
      flush();
      return true;
    }
    if (drawFrameRef.current !== null) { cancelAnimationFrame(drawFrameRef.current); drawFrameRef.current = null; }
    // Exactly one write per gesture, here.
    const wrote = next ? commit(d.docId, next) : false;
    // The write reserves the render synchronously (the session's subscription
    // calls the runner, which marks the slot `rendering` before it debounces),
    // so reading the slot back HERE is what says a render is actually coming.
    // Not every write produces one: a template unloaded inside the debounce
    // window stops the render with no generation move and no error, and a ghost
    // held on that would stay painted over the canvas for good.
    const after = useStore.getState().docs.find((x) => x.id === doc?.id)?.mockup;
    const reserved = onBack ? !!after?.backRendering : !!after?.rendering;
    // Nothing written (the drag ended where it began), or nothing coming: there
    // is no render for the ghost to wait for, so it goes now.
    ghostRef.current = wrote && reserved && next && ghostRef.current
      ? { ...ghostRef.current, ...next, held: true, at: renderedGeneration }
      : null;
    bump();
    // Fetched again for the next gesture: the commit may have been the last one
    // this blank serves, but the next drag usually follows within a second.
    warmBlank();
    return true;
  };

  /**
   * Arrow keys and `[`/`]` on the focused canvas (audit #12: placing art was a
   * mouse drag or nothing).
   *
   * Written through the SAME single `commit` a drag ends with, so a key press and
   * a drag cannot disagree about what a job write is — one store write, one
   * render, one undo entry, and consecutive presses coalesce into that one entry
   * (the store keys it by which field moved), so a held-down arrow is one Ctrl+Z
   * back to where the art started rather than forty.
   *
   * Nothing is written when nothing would move: `nudgePlacement` hands the job
   * straight back at the clamp, and `commit` returns false for a placement that
   * did not change.
   */
  const onKeyDown = (e: ReactKeyboardEvent<HTMLCanvasElement>): boolean => {
    if (!active || !template || !shownId || !job || !art) return false;
    // The brackets are read FIRST, because on a German, French or Nordic layout the
    // real `[` is AltGr+8 — which arrives with `altKey` set (and `ctrlKey` too on
    // Windows) and would be thrown away by the modifier check below. `bracketTurn`
    // keeps Ctrl+[ (the browser's Back) and every Meta combination out of the app.
    const bracket = bracketTurn(e);
    // Otherwise a modifier means the browser's or the app's own shortcut (Ctrl+Z, a
    // zoom): Shift is the only one that belongs to a nudge.
    if (!bracket && (e.ctrlKey || e.metaKey || e.altKey)) return false;
    const step = e.shiftKey ? NUDGE_SHIFT_IN : NUDGE_IN;
    const turn = e.shiftKey ? NUDGE_SHIFT_DEG : NUDGE_DEG;
    let dx = 0, dy = 0, deg = 0;
    // The arrows have no shifted form, so they are matched on `key`; the brackets
    // have, which is why they are `bracketTurn`'s job above.
    switch (e.key) {
      case 'ArrowLeft': dx = -step; break;
      case 'ArrowRight': dx = step; break;
      case 'ArrowUp': dy = -step; break;
      case 'ArrowDown': dy = step; break;
      default: {
        if (!bracket) return false;
        deg = bracket * turn;
      }
    }
    // Taken whether or not it moves the art: the arrow keys would otherwise
    // scroll the panel behind the canvas on every press at the clamp.
    e.preventDefault();
    // From the job THIS view shows, and committed to the document that owns it —
    // the same pair a drag uses, so the back view's derived job nudges exactly
    // where dragging it would. (`commit` re-reads the live job before writing, so
    // a colour change landing between two presses is not undone by the second.)
    const next = nudgePlacement(job, dx, dy, deg, { template, art });
    commit(shownId, { placement: next.placement, rotationDeg: next.rotationDeg });
    return true;
  };

  const onPointerCancel = (e: ReactPointerEvent<HTMLCanvasElement>): boolean => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return false;
    // A cancelled gesture has no ending to commit — the pointer was taken away
    // (a system gesture, the device lost). Drop the drag AND anything it had
    // queued or drawn rather than writing a placement the user never released on.
    abandon();
    return true;
  };

  return {
    active,
    preview,
    template,
    rendering: onBack ? !!mockup?.backRendering : !!mockup?.rendering,
    stale,
    clipped: onBack ? !!mockup?.backClipped : !!mockup?.clipped,
    view,
    canToggleView,
    setView: useCallback((next: MockupSide) => setViewer({ mockupView: next }), [setViewer]),
    corners,
    rotationDeg,
    extraBoxes,
    skipped: (onBack ? mockup?.backSkipped : mockup?.skipped) ?? [],
    blank,
    ghostArt,
    ghosting,
    frame,
    glFrame,
    glVersion,
    // The brief's surface, and NOT what the UI reads: the inspector row and the banner read the
    // store's `glPreview`, which is the sticky app-wide answer. This one is per-frame.
    renderer: glFrame ? 'gl' : 'cpu',
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onKeyDown,
  };
}
